import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useMemo, useState } from "react";
import { monthsAbbreviated } from "src/constants";
import { CalcularPaybackRoiProps, ProjetosProvider } from "src/providers/ProjetosProvider";
import { GraficoGeracaoConsumo, GeracaoConsumoProps } from "./containers/GraficoGeracaoConsumo";
import { GraficoPayback, PaybackProps } from "./containers/GraficoPayback";
import { useObservable } from "src/framework/store/storeUtil";
import { dimensionadoStore$ } from "src/store/vendas/projetos/store";
import { DimensionadoStoreType } from "src/store/vendas/projetos/types";
import { setRoiPaybackModalStatus } from "src/store/vendas/projetos/actions";
import theme from "src/theme/light";
import { Formater, TypeRecords } from "src/framework/commons/Formater";
import { PremissasGeracao } from "./containers/GraficoGeracaoConsumo/PremissasGeracao";
import { PremissasPayback } from "./containers/GraficoPayback/PremissasPayback";

const useStyles = makeStyles({
  listItemText: {
    margin: 0,
    color: 'black',
  },
  list: {
    marginTop: 50,
  },
  orderedListItem: {
    color: 'black !important'
  }
})

const projetosProvider = new ProjetosProvider()
export function GraficosRoiPayback(props: any){
  const dimensionadoStore = useObservable<DimensionadoStoreType>(dimensionadoStore$)

  const payload = dimensionadoStore?.roiPaybackModalStatus.payload
  const isOpened = dimensionadoStore?.roiPaybackModalStatus.isOpened

  const classes = useStyles();
  
  const [loading, setLoading] = useState(false)
  const [geracaoConsumo, setGeracaoConsumo] = useState<GeracaoConsumoProps[]>([])
  const [retornoInvestimento, setRetornoInvestimento] = useState<PaybackProps[]>([])
  const [geracaoTotal, setGeracaoTotal] = useState(0)
  const [consumoTotal, setConsumoTotal] = useState(0)
  const [instalacao, setInstalacao] = useState(false);

  const paybackRange = useMemo(()=>{
    const positiveNumbers = retornoInvestimento.filter(item => Number(item["Retorno do Investimento"] >= 0))
    const negativeNumbers = retornoInvestimento.filter(item => Number(item["Retorno do Investimento"] < 0))
    const range2 = positiveNumbers.shift();
    const range1 = negativeNumbers.pop();
    const range1Ano = range1?.ano
    const range2Ano = range2?.ano
    if (!range1Ano === undefined || !range2Ano === undefined) return ''
    if (range2Ano === 0) return `${1} Ano`
    if (range1Ano === range2Ano) return `${range1Ano} Ano(s)`
    return `Entre ${range1Ano} e ${range2Ano} Ano(s)`
  },[retornoInvestimento])

  useEffect(()=>{
    if (!payload?.projetoId && Object.keys(payload?.calculateData || {}).length === 0) return;
    setLoading(true)

    // força recalculo de roypayback
    const calculateData = {...payload?.calculateData, recalcular: true} as CalcularPaybackRoiProps;

    projetosProvider.calcularRoiPayback({ projetoId: payload?.projetoId, calculateData }).then((res) => {
      const geracao = res.data?.geracao_mensal || {}
      const consumo = res.data?.consumo || {}
      let consumo_total = 0;
      setGeracaoConsumo(Object.keys(geracao).map(geracaoItem => ({
        mes: monthsAbbreviated[(Number(geracaoItem) - 1)],
        Geração: geracao[geracaoItem],
        Consumo: consumo?.[geracaoItem] || 0
      })))

      setGeracaoTotal(res.data?.geracao_total || 0)

      if (consumo){
        for (const idx in consumo) {
          if (consumo.hasOwnProperty(idx)) {
            consumo_total += consumo[idx];
          }
        }
      }
      setConsumoTotal(consumo_total);

      if(props.valor_servico > 0){
        setInstalacao(true);
      }
      
      setRetornoInvestimento(res.data?.dados?.map(item => ({
        ano: item.ano,
        'Retorno do Investimento': item.retorno_investimento
      })) || [])
    }).finally(() => {
      setLoading(false)
    })
  },[payload])

  return (
    <Dialog
      open={isOpened || false}
      onClose={() => setRoiPaybackModalStatus(false)}
      maxWidth={'xl'}
  >
      <DialogTitle id="alert-dialog-title" />
      <DialogContent className="content-modal">
          <>
            {loading ? (
               <Box display='flex' alignItems={'center'} justifyContent={'center'} width={"1200px"} height={"500px"}>
                  <span>Carregando...</span>
               </Box>
            ) : (
              <>
              <Box width={"1200px"} height={"300px"} paddingBottom={"60px"}>
                {geracaoConsumo && (
                  <>
                    <span style={{ display: 'block', textAlign: 'center', marginBottom: 20, fontSize: 18 }}><strong>Geração do Sistema</strong></span>
                    <GraficoGeracaoConsumo data={geracaoConsumo} />            
                    <span style={{ display: 'block', textAlign: 'end', width: '100%' }} ><strong style={{ color: theme.palette.primary.main }}>Geração Solar Total:</strong> {Number(Number(geracaoTotal).toFixed(2)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh/ano</span>
                    <span style={{ display: 'block', textAlign: 'end', width: '100%' }} ><strong style={{ color: theme.palette.primary.main }}>Consumo Total:</strong> {Number(Number(consumoTotal).toFixed(2)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} kWh/ano</span>
                  </>
                )}
                <Box>
                  <p className={classes.listItemText}><strong><span style={{ color: 'red' }}>*</span> Informações importantes!</strong></p>
                  <PremissasGeracao                    
                    geracao_total={geracaoTotal}
                    consumo_total={consumoTotal}
                  />
                </Box>
              </Box>
              <Box width={"1200px"} height={"300px"}>
                {retornoInvestimento && (
                  <>
                    <span style={{ display: 'block', textAlign: 'center', margin: '200px 0 20px 0', fontSize: 18 }}><strong>Gráfico de tempo de retorno sobre o investimento (Payback)</strong></span>
                    <GraficoPayback data={retornoInvestimento} />
                    {paybackRange && <span style={{ display: 'block', textAlign: 'end', width: '100%'}} ><strong>Payback Aproximado</strong>: {paybackRange}</span>}
                  </>
                  )}
                <PremissasPayback 
                  instalacao={instalacao}
                />
              </Box>
            </>
            )}
          </>
      </DialogContent>
      <DialogActions>
          <Button onClick={() => setRoiPaybackModalStatus(false)} color="primary">
              Fechar
          </Button>
      </DialogActions>
  </Dialog>
  )
}